import { call, put, takeLatest, fork, delay } from "redux-saga/effects";
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  authCheckStart,
  authCheckSuccess,
  authCheckFailed,
  failed,
  authLogoutStart,
  authLogoutSuccess,
  getUserInfo,
  forgotPasswordStart,
  forgotPasswordSuccess,
  forgotPasswordFailed,
} from "./auth-slice";
import { PayloadAction } from "@reduxjs/toolkit";
import { loginResponse } from "../../../interfaces/store/auth-interfaces";
import { tokenApi, getUserInfoApi, forgotPasswordApi } from "../../../api/auth";
import { jwtDecode } from "jwt-decode";
import { getTokenRefreshTime } from "../../../utils/utils";
import { ILoginForm } from "../../../interfaces/components/forms/login";
import { updateToken, removeToken } from "../../../axios";
import { DecodedData } from "../../../utils/utils";
import { IBaseResponse } from "../../../interfaces/base";

const EXPIRATION_TIME = 600;

function* sendLoginRequest(action: PayloadAction<ILoginForm>) {
  try {
    const { email, password } = action.payload;
    const { data, status, error }: loginResponse = yield call(tokenApi, {
      grant_type: "password",
      email: email,
      password: password,
    });

    if (error) {
      yield put(loginFailure({ message: "Login Failed" }));
      return;
    }

    if (status === 200 && data.access_token) {
      const { access_token, id_token, refresh_token, expires_in } = data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("id_token", id_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("expires_in", JSON.stringify(expires_in));

      yield call(updateToken, access_token);
      const refreshTime = getTokenRefreshTime(access_token);
      yield fork(scheduleTokenRefresh, refreshTime);

      yield put(getUserInfo());
    }
  } catch (error: any) {
    if (error.response.data.error === "USERNAME OR PASSWORD IS INCORRECT") {
      yield put(
        loginFailure({
          status: 4000,
          message: "Invalid credentials. Try again.",
        })
      );
    } else {
      yield put(loginFailure({ message: "Failed to Log In" }));
    }
  }
}

function* authCheckState(): any {
  try {
    const token = localStorage.getItem("access_token");
    const id_token = localStorage.getItem("id_token");
    const refresh_token = localStorage.getItem("refresh_token");
    if (!token || !refresh_token) {
      localStorage.clear();
      return yield put(authLogoutSuccess());
    }
    const decoded: DecodedData = jwtDecode(token);
    const expiration = decoded.exp;
    const refreshTime = getTokenRefreshTime(token);

    if (
      (!expiration || expiration - 300 < new Date().getTime() / 1000) &&
      refresh_token
    ) {
      const { status, data, error } = yield call(tokenApi, {
        grant_type: "refresh_token",
        refresh_token,
      });
      if (status === 200 && data.access_token) {
        const { access_token, id_token, refresh_token, expires_in } = data;
        localStorage.setItem("access_token", access_token);
        localStorage.setItem("id_token", id_token);
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem("expires_in", JSON.stringify(expires_in));

        yield call(updateToken, access_token);
        yield fork(scheduleTokenRefresh, refreshTime);
        const user = getDecodedInfo(id_token);
        yield put(getUserInfo());
      } else {
        // localStorage.clear();
        removeToken();
        return yield put(authLogoutSuccess());
      }
    } else {
      yield fork(loadTokens);
    }
  } catch (error: any) {
    console.log("catch error", error);

    return yield put(authCheckFailed({ message: "Failed to check auth" }));
  }
}

function* authUserLogout(): any {
  try {
    localStorage.clear();
    yield call(removeToken);
    yield put(authLogoutSuccess());
  } catch (error: any) {
    console.log(error);
    yield put(failed({ message: error.message }));
  }
}

export const getDecodedInfo = (token: string | null) => {
  try {
    if (token) {
      const decoded = jwtDecode(token);
      return decoded;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
};

function* loadTokens() {
  try {
    let refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      return "refresh token not found";
    }

    const { status, data, error } = yield call(tokenApi, {
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    });

    if (status === 200 && data.access_token) {
      const { access_token, id_token, refresh_token, expires_in } = data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("id_token", id_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("expires_in", JSON.stringify(expires_in));
      yield call(updateToken, access_token);

      const time = getTokenRefreshTime(data.access_token);
      yield put(getUserInfo());
      yield fork(scheduleTokenRefresh, time);
    }
    return error;
  } catch (error) {
    console.log(error);
    yield put(authLogoutStart());
    return error;
  }
}

function* scheduleTokenRefresh(timer: number): any {
  // console.log("Refresh in " + timer);
  try {
    yield delay(timer);

    const error = yield call(loadTokens);

    if (error) {
      yield put(authUserLogout());
    }
  } catch (error) {
    yield put(authUserLogout());
    return;
  }
}

// function* _getOtpSecret({ payload }: PayloadAction<string>) {
//   try {
//     const { data, status, error } = yield call(getOTPSecretApi, payload);

//     if (status === 200 && data) {
//       yield put(setOtpSecret(data.secret));
//     } else {
//       yield put(failed({ message: "Failed to get otp secret" }));
//     }
//   } catch (error: any) {
//     console.log(error);
//     yield put(failed({ message: error.message }));
//   }
// }

function* getUserInfos() {
  try {
    const { data, status }: IBaseResponse = yield call(getUserInfoApi);

    if (status === 200 && data) {
      const user = {
        email: data.email,
        name: data.fullName,
        _id: data.id,
        role: data.role,
      };

      yield put(loginSuccess(user));
    } else {
      localStorage.clear();
      yield put(loginFailure({ message: "Invalid token provided" }));
    }
  } catch (error: any) {
    console.log(error);

    yield put(authCheckFailed({ message: "Failed to get user info" }));
  }
}

function* _authLogoutSuccess() {
  localStorage.clear();
}

function* forgotPassword(action: PayloadAction<{ email: string }>) {
  try {
    const { email } = action.payload;
    const { data, status, error } = yield call(forgotPasswordApi, email);

    if (status === 200 && data) {
      yield put(forgotPasswordSuccess(data));
    } else {
      yield put(forgotPasswordFailed(error));
    }
  } catch (error: any) {
    if (error.response.data.error === "ACCOUNT NOT FOUND") {
      yield put(
        forgotPasswordFailed({
          status: 3000,
          message: "Invalid credentials. Try again.",
        })
      );
    } else {
      yield put(forgotPasswordFailed(error.response.data.error));
    }
  }
}

function* authSaga() {
  yield takeLatest(loginRequest.type, sendLoginRequest);

  yield takeLatest(authCheckStart.type, authCheckState);
  yield takeLatest(authCheckSuccess.type, authCheckState);
  yield takeLatest(authLogoutStart.type, authUserLogout);
  yield takeLatest(authLogoutSuccess.type, _authLogoutSuccess);
  yield takeLatest(getUserInfo.type, getUserInfos);
  yield takeLatest(forgotPasswordStart.type, forgotPassword);
}

export default authSaga;
